import { PageUrlElementModel, PageUrlLinkedEntityModel, PageUrlType } from './types';
import { i18nKeys } from './constants';
import { BadgeColorVariant } from '../../../components/common/StatusBadge';

/**
 * Normalise urlType to the numeric enum. The backend may send it as a number
 * (1–7) or a lowercase name ("campaign", "team", "superteam"…).
 */
export const normaliseUrlType = (
  urlType: PageUrlElementModel['urlType']
): PageUrlType | undefined => {
  const key = String(urlType).toLowerCase();
  if (key === 'organization' || key === '1') return PageUrlType.Organization;
  if (key === 'campaign' || key === '2') return PageUrlType.Campaign;
  if (key === 'event' || key === '3') return PageUrlType.Event;
  if (key === 'fundraiser' || key === '4') return PageUrlType.Fundraiser;
  if (key === 'team' || key === '5') return PageUrlType.Team;
  if (key === 'collection' || key === 'superteam' || key === '6') return PageUrlType.Collection;
  if (key === 'series' || key === '7') return PageUrlType.Series;
  return undefined;
};

export const getRecordEntity = (
  item: PageUrlElementModel
): PageUrlLinkedEntityModel | undefined => {
  switch (normaliseUrlType(item.urlType)) {
    case PageUrlType.Organization: return item.organization;
    case PageUrlType.Campaign: return item.campaign;
    case PageUrlType.Event: return item.event;
    case PageUrlType.Fundraiser: return item.fundraiser;
    case PageUrlType.Team: return item.team;
    case PageUrlType.Collection: return item.collection;
    case PageUrlType.Series: return item.series;
    default: return undefined;
  }
};

export const getEntityTypeI18nKey = (
  urlType: PageUrlElementModel['urlType']
): string | undefined => {
  switch (normaliseUrlType(urlType)) {
    case PageUrlType.Organization: return i18nKeys.RESULTS.ORGANIZATION;
    case PageUrlType.Campaign: return i18nKeys.RESULTS.CAMPAIGN;
    case PageUrlType.Event: return i18nKeys.RESULTS.EVENT;
    case PageUrlType.Fundraiser: return i18nKeys.RESULTS.FUNDRAISER;
    case PageUrlType.Team: return i18nKeys.RESULTS.TEAM;
    case PageUrlType.Collection: return i18nKeys.RESULTS.SUPERTEAM;
    case PageUrlType.Series: return i18nKeys.RESULTS.SERIES;
    default: return undefined;
  }
};

export const statusToVariant = (status: string): BadgeColorVariant => {
  switch (status.toLowerCase()) {
    case 'live': return 'success';
    case 'complete': return 'info';
    case 'archived': return 'neutral';
    case 'deleted': return 'error';
    default: return 'neutral';
  }
};

/**
 * Organization and Series don't have a status field — backend returns null.
 */
export const hasStatus = (
  entity: PageUrlLinkedEntityModel
): entity is PageUrlLinkedEntityModel & { status: string } =>
  typeof entity.status === 'string' && entity.status.length > 0;
